import { useEffect, useRef } from "react";

/**
 * Lightweight canvas-based animated contour/wave field.
 * Pure math-driven — feels like a data/field visualisation, not gimmicky.
 */
export default function WaveCanvas({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let w = 0,
      h = 0,
      dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const LINES = 44;
    const POINTS = 90;

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < LINES; i++) {
        const yBase = (i / (LINES - 1)) * h;
        const alpha = 0.04 + 0.22 * Math.pow(1 - Math.abs(i / LINES - 0.5) * 2, 2);
        ctx.beginPath();
        for (let j = 0; j <= POINTS; j++) {
          const x = (j / POINTS) * w;
          const nx = j / POINTS;
          const wave =
            Math.sin(nx * 6 + t * 0.0006 + i * 0.18) *
              (18 + (i % 5) * 2) +
            Math.sin(nx * 13 - t * 0.0009 + i * 0.11) * 8;
          const y = yBase + wave * (0.3 + (i / LINES) * 0.9);
          if (j === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="hero-wave-canvas"
      className={`absolute inset-0 w-full h-full ${className}`}
      aria-hidden="true"
    />
  );
}
